<template>
  <div class="flex items-center w-full px-3 py-2 bg-white shadow-md rounded-md">
    <div class="mr-2 text-sm text-gray-600">
      {{ $t("Show") }}
    </div>
    <SelectInput
      id="show-selection"
      ref="selectInput"
      :open-to-top="openToTop"
      :value="page_size"
      :reverse="true"
      :is-box-background="true"
      class="w-[115px] rounded-md"
      :input-classes="'text-left'"
      name="page_show_options"
      :allow-null="false"
      :items="page_show_options"
      @update:currentItem="handleChangePageSize" />
    <div class="ml-2 text-sm text-gray-600" :title="object_count > 999 ? object_count.toLocaleString() : ''">
      {{ $t("in page") }} <span>({{ $utils.humanize(object_count) }} {{ $t("total") }})</span>
    </div>

    <nav
      v-if="object_count > page_size"
      class="inline-flex ml-auto items-center -space-x-px rounded-md select-none isolate"
      aria-label="Page navigation">
      <div class="mx-2 text-sm text-gray-600">
        {{ $t("Pages") }}
      </div>
      <ul class="flex gap-x-2">
        <li v-if="current_page !== 1" class="px-2.5 py-0.5 text-gray-600 cursor-pointer">
          <div id="prev-btn" @click.prevent="goToPage(current_page - 1)">
            <ChevronRight
              class="w-5 h-5 mt-0.5 text-gray-600"
              :class="[$store.state.portfolio.ui_options?.language == 'ar' ? '' : 'rotate-180']" />
          </div>
        </li>
        <li :class="[`px-2.5 py-0.5 text-gray-600 cursor-pointer ${current_page == 1 ? 'border border-blue-500 rounded-md' : ''}`]">
          <div id="page-1" @click.prevent="goToPage(1)">
            1
          </div>
        </li>
        <li v-if="current_page - 2 > 1" class="px-2.5 py-0.5 text-gray-600">
          <div id="ellipsis-prev">
            ...
          </div>
        </li>
        <li v-if="current_page > 2" class="px-2.5 py-0.5 text-gray-600 cursor-pointer">
          <div id="page-prev" @click.prevent="goToPage(current_page - 1)">
            {{ current_page - 1 }}
          </div>
        </li>
        <li
          v-if="current_page !== 1 && current_page !== total_pages"
          class="text-gray-600 border border-blue-500 rounded-md px-2.5 py-0.5 cursor-pointer">
          <div id="current-page">
            {{ current_page }}
          </div>
        </li>
        <li v-if="current_page + 1 < total_pages" class="px-2.5 py-0.5 text-gray-600 cursor-pointer">
          <div id="page-next" @click.prevent="goToPage(current_page + 1)">
            {{ current_page + 1 }}
          </div>
        </li>
        <li v-if="current_page == 1 && current_page + 2 < total_pages" class="px-2.5 py-0.5 text-gray-600 cursor-pointer">
          <div id="page-next-2" @click.prevent="goToPage(current_page + 2)">
            {{ current_page + 2 }}
          </div>
        </li>
        <li v-if="current_page == 1 && current_page + 3 < total_pages" class="px-2.5 py-0.5 text-gray-600">
          <div id="ellipsis-next">
            ...
          </div>
        </li>
        <li v-if="current_page > 1 && current_page + 2 < total_pages" class="px-2.5 py-0.5 text-gray-600">
          <div id="ellipsis-next-mid">
            ...
          </div>
        </li>
        <li
          :class="[`px-2.5 py-0.5 text-gray-600 cursor-pointer ${current_page == total_pages ? 'border border-blue-500 rounded-md' : ''}`]">
          <div id="last-page" @click.prevent="goToPage(total_pages)">
            {{ total_pages }}
          </div>
        </li>
        <li v-if="current_page !== total_pages" class="px-2.5 py-0.5 text-gray-600 cursor-pointer">
          <div id="plus-btn" @click.prevent="goToPage(current_page + 1)">
            <ChevronRight
              class="w-5 h-5 mt-0.5 text-gray-600"
              :class="[$store.state.portfolio.ui_options?.language == 'ar' ? 'rotate-180' : '']" />
          </div>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script>
import SelectInput from "@/components/shared/SelectInput.vue"
import { ChevronRight } from "lucide-vue-next"

export default {
  components: {
    SelectInput,
    ChevronRight
  },
  props: {
    openToTop: null,
    object: null,
    defaultPageSize: {
      type: Number,
      default: 10
    },
    minPageSize: {
      type: Number,
      default: 10
    }
  },
  emits: ["update"],
  data() {
    return { current_page: 1, page_size: 10 }
  },
  computed: {
    object_count() {
      return this.object?.count || 0
    },
    total_pages() {
      return Math.ceil(this.object_count / this.page_size)
    },
    page_show_options() {
      const options = [10]
      if (this.minPageSize < 10) {
        options.unshift(5)
      }
      if (this.object_count > 10) {
        options.push(20)
      }
      if (this.object_count > 18) {
        options.push(30)
      }
      if (this.object_count > 28) {
        options.push(40)
      }
      if (this.object_count > 38) {
        options.push(50)
      }
      return options
    }
  },
  created() {
    this.page_size = this.defaultPageSize
  },
  methods: {
    handleChangePageSize(e) {
      this.page_size = e
      this.current_page = 1
      this.$emit("update", { page: this.current_page, page_size: this.page_size })
    },
    goToPage(page) {
      if (page < 1 || page > this.total_pages) {
        return
      }
      this.current_page = page
      this.$emit("update", { page: this.current_page, page_size: this.page_size })
    },
    formatNumber(number) {
      if (number > 999) {
        return (number / 1000).toFixed(1) + "k"
      }
      return number
    }
  }
}
</script>
